import React, { useState, useEffect } from 'react';
import { db } from '../firebase';
import { collection, getDocs } from 'firebase/firestore';
import { logError } from '../utils/log';

const gameStructure = [
    { name: '301 Singles', type: 'S301' }, { name: '301 Singles', type: 'S301' }, { name: '301 Doubles', type: 'D301' },
    { name: '501 Singles', type: 'S501' }, { name: '501 Singles', type: 'S501' }, { name: '501 Doubles', type: 'D501' },
    { name: 'Cricket Singles', type: 'SCricket' }, { name: 'Cricket Singles', type: 'SCricket' }, { name: 'Cricket Doubles', type: 'DCricket' },
];

export const StandingsPage = () => {
    const [standings, setStandings] = useState([]);
    const [loading, setLoading] = useState(true);
    const [sortConfig, setSortConfig] = useState({ key: 'gamePoints', direction: 'desc' });
    const [currentSchedule, setCurrentSchedule] = useState(null);
    const [allSchedules, setAllSchedules] = useState([]);
    const [selectedScheduleId, setSelectedScheduleId] = useState('');

    useEffect(() => {
        document.body.classList.add('stats-lock-x');
        document.documentElement.classList.add('stats-lock-x');
        return () => {
            document.body.classList.remove('stats-lock-x');
            document.documentElement.classList.remove('stats-lock-x');
        };
    }, []);

    useEffect(() => {
        const fetchSchedules = async () => {
            setLoading(true);
            try {
                const schedulesSnapshot = await getDocs(collection(db, "schedules"));
                const schedulesList = schedulesSnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
                schedulesList.sort((a, b) => {
                    if (a.archived !== b.archived) return a.archived ? 1 : -1;
                    return new Date(b.createdAt) - new Date(a.createdAt);
                });
                setAllSchedules(schedulesList);

                if (schedulesList.length > 0) {
                    const latestNonArchived = schedulesList.find(s => !s.archived);
                    setSelectedScheduleId(latestNonArchived ? latestNonArchived.id : schedulesList[0].id);
                } else {
                    setLoading(false);
                }
            } catch (error) {
                logError("Error fetching schedules: ", error);
                setLoading(false);
            }
        };
        fetchSchedules();
    }, []);

    useEffect(() => {
        if (!selectedScheduleId || allSchedules.length === 0) {
            if (allSchedules.length > 0) setLoading(false);
            return;
        }

        const calculateStandings = async () => {
            setLoading(true);
            try {
                const scheduleForStandings = allSchedules.find(s => s.id === selectedScheduleId);
                setCurrentSchedule(scheduleForStandings);

                if (!scheduleForStandings) {
                    setStandings([]);
                    setLoading(false);
                    return;
                }

                const teamsSnapshot = await getDocs(collection(db, "teams"));
                const allTeamsData = teamsSnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));

                const participatingTeamIds = new Set();
                if (Array.isArray(scheduleForStandings.teams) && scheduleForStandings.teams.length > 0) {
                    scheduleForStandings.teams.forEach(tid => tid && participatingTeamIds.add(tid));
                } else {
                    scheduleForStandings.weeks.forEach(week => {
                        (week.games || []).forEach(match => {
                            if (match.homeTeamId && match.homeTeamId !== 'TBD') participatingTeamIds.add(match.homeTeamId);
                            if (match.awayTeamId && match.awayTeamId !== 'TBD') participatingTeamIds.add(match.awayTeamId);
                        });
                    });
                }

                const teamsData = allTeamsData.filter(t => participatingTeamIds.has(t.id));

                const standingsData = {};
                teamsData.forEach(team => {
                    standingsData[team.id] = { id: team.id, name: team.name, gamePoints: 0, matchWins: 0, matchLosses: 0, weeksPlayed: new Set(), S301: { legsWon: 0, legsPlayed: 0, w: 0, l: 0 }, D301: { legsWon: 0, legsPlayed: 0, w: 0, l: 0 }, S501: { legsWon: 0, legsPlayed: 0, w: 0, l: 0 }, D501: { legsWon: 0, legsPlayed: 0, w: 0, l: 0 }, SCricket: { legsWon: 0, legsPlayed: 0, w: 0, l: 0 }, DCricket: { legsWon: 0, legsPlayed: 0, w: 0, l: 0 }, };
                });

                scheduleForStandings.weeks.forEach(week => {
                    const weekIdentifier = `${scheduleForStandings.id}-${week.week}`;
                    week.games.forEach(match => {
                        if (match.results) {
                            const homeTeamId = match.homeTeamId;
                            const awayTeamId = match.awayTeamId;
                            let homeMatchGamePoints = 0;
                            let awayMatchGamePoints = 0;

                            const homeEntry = standingsData[homeTeamId];
                            const awayEntry = standingsData[awayTeamId];

                            if (homeEntry) homeEntry.weeksPlayed.add(weekIdentifier);
                            if (awayEntry) awayEntry.weeksPlayed.add(weekIdentifier);

                            match.results.gameScores.forEach((score, idx) => {
                                const gameType = gameStructure[idx].type;

                                if (homeEntry) {
                                    homeEntry[gameType].legsWon += score.home;
                                    homeEntry[gameType].legsPlayed += score.home + score.away;
                                }
                                if (awayEntry) {
                                    awayEntry[gameType].legsWon += score.away;
                                    awayEntry[gameType].legsPlayed += score.home + score.away;
                                }

                                if (score.home > score.away) {
                                    homeMatchGamePoints++;
                                    if (homeEntry) homeEntry[gameType].w++;
                                    if (awayEntry) awayEntry[gameType].l++;
                                } else if (score.away > score.home) {
                                    awayMatchGamePoints++;
                                    if (awayEntry) awayEntry[gameType].w++;
                                    if (homeEntry) homeEntry[gameType].l++;
                                }
                            });

                            if (homeEntry) homeEntry.gamePoints += homeMatchGamePoints;
                            if (awayEntry) awayEntry.gamePoints += awayMatchGamePoints;
                            if (homeMatchGamePoints > awayMatchGamePoints) {
                                if (homeEntry) homeEntry.matchWins++;
                                if (awayEntry) awayEntry.matchLosses++;
                            } else if (awayMatchGamePoints > homeMatchGamePoints) {
                                if (awayEntry) awayEntry.matchWins++;
                                if (homeEntry) homeEntry.matchLosses++;
                            }
                        }
                    });
                });

                const standingsArray = Object.values(standingsData);
                standingsArray.forEach(team => {
                    team.weeksPlayed = team.weeksPlayed.size;
                });
                setStandings(standingsArray);

            } catch (error) {
                logError("Error calculating standings: ", error);
            }
            setLoading(false);
        };

        calculateStandings();
    }, [selectedScheduleId, allSchedules]);

    const sortedStandings = React.useMemo(() => {
        let sortableItems = [...standings];
        if (sortConfig !== null) {
            sortableItems.sort((a, b) => {
                const aValue = a[sortConfig.key];
                const bValue = b[sortConfig.key];

                if (aValue < bValue) {
                    return sortConfig.direction === 'asc' ? -1 : 1;
                }
                if (aValue > bValue) {
                    return sortConfig.direction === 'asc' ? 1 : -1;
                }

                // If primary sort key is gamePoints and they are tied, use head-to-head as a tie-breaker.
                if (sortConfig.key === 'gamePoints') {
                    if (currentSchedule && currentSchedule.weeks) {
                        for (const week of currentSchedule.weeks) {
                            for (const game of week.games) {
                                const isMatch = (game.homeTeamId === a.id && game.awayTeamId === b.id) || (game.homeTeamId === b.id && game.awayTeamId === a.id);
                                if (isMatch && game.results && game.results.matchStats) {
                                    const homePoints = game.results.matchStats.home.gamePoints;
                                    const awayPoints = game.results.matchStats.away.gamePoints;
                                    if (homePoints !== awayPoints) {
                                        const winnerId = homePoints > awayPoints ? game.homeTeamId : game.awayTeamId;
                                        if (winnerId === a.id) return -1; // a wins, ranked higher
                                        if (winnerId === b.id) return 1;  // b wins, ranked higher
                                    }
                                }
                            }
                        }
                    }
                }

                return a.name.localeCompare(b.name); // Final alphabetical sort for stability
            });
        }
        return sortableItems;
    }, [standings, sortConfig, currentSchedule]);

    const requestSort = (key) => {
        let direction = 'desc';
        if (sortConfig.key === key && sortConfig.direction === 'desc') {
            direction = 'asc';
        }
        setSortConfig({ key, direction });
    };

    const getSortIndicator = (key) => {
        if (sortConfig.key === key) {
            return sortConfig.direction === 'desc' ? ' ▼' : ' ▲';
        }
        return '';
    };

    if (loading) {
        return <div className="text-center">Loading standings...</div>;
    }

    return (
        <div className="space-y-6">
            <div className="flex flex-col sm:flex-row justify-between items-center gap-4">
                <h2 className="page-title heading-primary-1 m-0">League Standings</h2>
                <div className="flex gap-2 items-center w-full sm:w-auto">
                    <label htmlFor="season-select" className="text-sm font-semibold text-muted whitespace-nowrap">Season:</label>
                    <select
                        id="season-select"
                        value={selectedScheduleId}
                        onChange={e => setSelectedScheduleId(e.target.value)}
                        className="select-field p-2 w-full sm:w-auto"
                        disabled={loading || allSchedules.length === 0}
                    >
                        {allSchedules.map(s => (
                            <option key={s.id} value={s.id}>
                                {s.name} {s.archived ? '(Archived)' : ''}
                            </option>
                        ))}
                    </select>
                </div>
            </div>
            {currentSchedule?.archived && (<p className="text-center text-sm text-warning -mt-4">You are viewing standings for an archived season.</p>)}
            <div className="table-wrap">
                <table className="min-w-max sm:min-w-full">
                    <thead className="table-header">
                        <tr>
                            <th rowSpan="2" aria-label="Rank" className="sticky left-0 sticky-cell px-2 py-3 text-center text-xs font-medium text-muted uppercase tracking-wider z-20 w-12"></th>
                            <th rowSpan="2" className="sticky left-12 sticky-cell px-4 py-3 text-left text-xs font-medium text-muted uppercase tracking-wider z-10 cursor-pointer" onClick={() => requestSort('name')}>Team{getSortIndicator('name')}</th>
                            <th rowSpan="2" className="px-4 py-3 text-center text-xs font-medium text-muted uppercase tracking-wider cursor-pointer" onClick={() => requestSort('weeksPlayed')}>WP{getSortIndicator('weeksPlayed')}</th>
                            <th rowSpan="2" className="px-4 py-3 text-center text-xs font-medium text-muted uppercase tracking-wider cursor-pointer" onClick={() => requestSort('matchWins')}>W-L{getSortIndicator('matchWins')}</th>
                            <th rowSpan="2" className="px-4 py-3 text-center text-xs font-medium text-muted uppercase tracking-wider cursor-pointer" onClick={() => requestSort('gamePoints')}>Points{getSortIndicator('gamePoints')}</th>
                            <th colSpan="2" className="px-4 py-3 text-center text-xs font-medium text-muted uppercase tracking-wider border-low">301</th>
                            <th colSpan="2" className="px-4 py-3 text-center text-xs font-medium text-muted uppercase tracking-wider border-low">501</th>
                            <th colSpan="2" className="px-4 py-3 text-center text-xs font-medium text-muted uppercase tracking-wider">Cricket</th>
                        </tr>
                        <tr>
                            <th className="px-2 py-2 text-center text-xs font-medium text-muted uppercase tracking-wider border-low">S (W-L / Legs)</th>
                            <th className="px-2 py-2 text-center text-xs font-medium text-muted uppercase tracking-wider border-low">D (W-L / Legs)</th>
                            <th className="px-2 py-2 text-center text-xs font-medium text-muted uppercase tracking-wider">S (W-L / Legs)</th>
                            <th className="px-2 py-2 text-center text-xs font-medium text-muted uppercase tracking-wider border-low">D (W-L / Legs)</th>
                            <th className="px-2 py-2 text-center text-xs font-medium text-muted uppercase tracking-wider">S (W-L / Legs)</th>
                            <th className="px-2 py-2 text-center text-xs font-medium text-muted uppercase tracking-wider">D (W-L / Legs)</th>
                        </tr>
                    </thead>
                    <tbody>
                        {sortedStandings.map((team, index) => (
                            <tr key={team.id} className={index % 2 === 1 ? 'row-alt' : ''}>
                                <td className="sticky left-0 sticky-cell px-2 py-4 whitespace-nowrap text-sm font-medium text-strong z-20 w-12 text-center">{index + 1}</td>
                                <td className="sticky left-12 sticky-cell px-4 py-4 whitespace-nowrap text-sm font-semibold text-strong z-10">{team.name}</td>
                                <td className="px-4 py-4 whitespace-nowrap text-sm text-center text-muted">{team.weeksPlayed}</td>
                                <td className="px-4 py-4 whitespace-nowrap text-sm text-center text-muted">{team.matchWins}-{team.matchLosses}</td>
                                <td className="px-4 py-4 whitespace-nowrap text-sm text-center font-bold"><span className="tag tag-primary-one">{team.gamePoints}</span></td>
                                <td className="px-4 py-4 whitespace-nowrap text-sm text-center text-muted border-low">{team.S301.w}-{team.S301.l} <span className="text-xs text-muted">({team.S301.legsWon}-{team.S301.legsPlayed - team.S301.legsWon})</span></td>
                                <td className="px-4 py-4 whitespace-nowrap text-sm text-center text-muted border-low">{team.D301.w}-{team.D301.l} <span className="text-xs text-muted">({team.D301.legsWon}-{team.D301.legsPlayed - team.D301.legsWon})</span></td>
                                <td className="px-4 py-4 whitespace-nowrap text-sm text-center text-muted">{team.S501.w}-{team.S501.l} <span className="text-xs text-muted">({team.S501.legsWon}-{team.S501.legsPlayed - team.S501.legsWon})</span></td>
                                <td className="px-4 py-4 whitespace-nowrap text-sm text-center text-muted border-low">{team.D501.w}-{team.D501.l} <span className="text-xs text-muted">({team.D501.legsWon}-{team.D501.legsPlayed - team.D501.legsWon})</span></td>
                                <td className="px-4 py-4 whitespace-nowrap text-sm text-center text-muted">{team.SCricket.w}-{team.SCricket.l} <span className="text-xs text-muted">({team.SCricket.legsWon}-{team.SCricket.legsPlayed - team.SCricket.legsWon})</span></td>
                                <td className="px-4 py-4 whitespace-nowrap text-sm text-center text-muted">{team.DCricket.w}-{team.DCricket.l} <span className="text-xs text-muted">({team.DCricket.legsWon}-{team.DCricket.legsPlayed - team.DCricket.legsWon})</span></td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>
        </div>
    );
};